import { api } from "./api";

const POINT_RE = /\[\[point:([^\]]+)\]\]/;

export const splitPoint = (text = "") => {
  const m = text.match(POINT_RE);
  return { text: text.replace(/\[\[point:[^\]]*\]\]/g, "").trim(), point: m ? m[1].trim() : null };
};

// Visible clickable elements (with data-testid) the assistant may point at.
export const collectUi = () => {
  const seen = new Set();
  const out = [];
  document.querySelectorAll("button[data-testid], a[data-testid], [role='button'][data-testid], [role='tab'][data-testid]").forEach((el) => {
    if (out.length >= 80 || el.closest("[data-testid='baraa-panel']")) return;
    const id = el.getAttribute("data-testid");
    if (seen.has(id)) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const label = (el.getAttribute("aria-label") || el.innerText || el.getAttribute("title") || "").replace(/\s+/g, " ").trim().slice(0, 50);
    if (!label) return;
    seen.add(id);
    out.push({ id, label });
  });
  return out;
};

// Spotlight + "اضغط هنا" pointer on the target element for a few seconds.
export const pointTo = (id) => {
  const el = [...document.querySelectorAll(`[data-testid="${CSS.escape(id)}"]`)]
    .find((e) => { const r = e.getBoundingClientRect(); return r.width > 0 && r.height > 0; });
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  document.getElementById("baraa-pointer")?.remove();
  const box = document.createElement("div");
  box.id = "baraa-pointer";
  box.setAttribute("data-testid", "baraa-pointer");
  box.innerHTML = `<style>
@keyframes bp-pulse{0%,100%{box-shadow:0 0 0 4px rgba(14,165,233,.55),0 0 0 9999px rgba(15,23,42,.28)}50%{box-shadow:0 0 0 9px rgba(14,165,233,.25),0 0 0 9999px rgba(15,23,42,.28)}}
@keyframes bp-bob{0%,100%{transform:translate(-50%,0)}50%{transform:translate(-50%,-6px)}}
</style><div class="bp-ring" style="position:fixed;border-radius:12px;pointer-events:none;animation:bp-pulse 1.2s ease-in-out infinite;z-index:70;transition:all .2s"></div>
<div class="bp-tip" style="position:fixed;z-index:71;pointer-events:none;animation:bp-bob .9s ease-in-out infinite;background:#0ea5e9;color:#fff;font-family:inherit;font-weight:700;font-size:12px;line-height:1.2;padding:6px 10px;border-radius:999px;white-space:nowrap;box-shadow:0 6px 16px rgba(14,165,233,.4)" dir="rtl">اضغط هنا ↓</div>`;
  box.style.fontFamily = getComputedStyle(document.querySelector("#root button") || document.body).fontFamily;
  document.body.appendChild(box);
  const ring = box.querySelector(".bp-ring"), tip = box.querySelector(".bp-tip");
  const place = () => {
    const r = el.getBoundingClientRect();
    Object.assign(ring.style, { left: `${r.left - 6}px`, top: `${r.top - 6}px`, width: `${r.width + 12}px`, height: `${r.height + 12}px` });
    Object.assign(tip.style, { left: `${r.left + r.width / 2}px`, top: `${Math.max(4, r.top - 40)}px` });
  };
  place();
  const iv = setInterval(place, 120);
  const done = () => { clearInterval(iv); box.remove(); el.removeEventListener("click", done); };
  el.addEventListener("click", done);
  setTimeout(done, 7000);
  return true;
};

// Time-of-day greeting (Riyadh) + the most important unread alert.
export const timeGreeting = async () => {
  const h = Number(new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: "Asia/Riyadh" }).format(new Date()));
  const hello = h >= 4 && h < 12 ? "صباح الخير" : "مساء الخير";
  let alert = "";
  try {
    const { items = [] } = await api.notifications();
    const n = items.find((x) => !x.read);
    if (n) {
      const map = {
        opcard_expiry: () => `${n.count} كرت تشغيل يقترب من الانتهاء${n.cards?.[0] ? ` (أقربها ${n.cards[0].number} بعد ${n.cards[0].days_left} يوم)` : ""}`,
        fuel_suspicious: () => `${n.count} تعبئة وقود مشبوهة في ملف واعي ${n.month || ""}`,
        password_reset: () => `طلب استعادة كلمة مرور من ${n.employee_name || "مستخدم"}`,
        low_stock: () => `مخزون منخفض: ${n.employee_name || ""}`,
        advance_request: () => `طلب سلفة من ${n.employee_name} بمبلغ ${n.amount} ريال`,
        private_doc_expiry: () => n.message,
        card_received: () => `${n.employee_name} أكّد استلام بطاقة المندوب`,
        violation_added: () => `مخالفة جديدة على ${n.employee_name} بمبلغ ${n.amount} ريال`,
      };
      alert = (map[n.type] || (() => `${n.employee_name || "تنبيه جديد"}`))();
    }
  } catch { /* not a company user */ }
  return `${hello}! أنا **براء** مساعدك في WorkZone.${alert ? `\n\nأهم تنبيه اليوم: **${alert}** — اسألني وأشرح لك ما تفعله.` : "\n\nلا توجد تنبيهات عاجلة اليوم. اسألني عن أي خطوة في النظام."}`;
};

let audioEl = null;
export const stopVoice = () => { if (audioEl) { audioEl.pause(); audioEl = null; } window.speechSynthesis?.cancel(); };

// Natural voice (OpenAI TTS); falls back to the browser voice if unavailable.
export const playVoice = async (raw, onEnd) => {
  stopVoice();
  const plain = (raw || "").replace(/<[^>]+>/g, "").replace(/\*\*/g, "").replace(/\[\[point:[^\]]*\]\]/g, "").trim();
  if (!plain) { onEnd?.(); return; }
  try {
    const blob = await api.baraaTts(plain);
    const url = URL.createObjectURL(blob);
    audioEl = new Audio(url);
    audioEl.onended = audioEl.onerror = () => { URL.revokeObjectURL(url); audioEl = null; onEnd?.(); };
    await audioEl.play();
  } catch {
    const synth = window.speechSynthesis;
    if (!synth) { onEnd?.(); return; }
    const u = new SpeechSynthesisUtterance(plain.replace(/\n+/g, ". "));
    u.lang = "ar-SA";
    u.onend = u.onerror = () => onEnd?.();
    synth.speak(u);
  }
};
